class SafeLocalStorage<V> {
  private isClient: boolean;
  private storageAvailable: boolean | null;
  private key: string;

  constructor(key: string, defaultValue?: V) {
    this.isClient = typeof window !== 'undefined';
    this.storageAvailable = null;
    this.key = key;
    if (this.isLocalStorageAvailable() && defaultValue !== undefined) {
      if (!localStorage.getItem(key)) {
        this.set(defaultValue);
      }
    }
  }

  static isStorageAvailable(): boolean {
    if (typeof window === 'undefined') return false;

    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      const testValue = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);

      return testValue === testKey;
    } catch (e) {
      console.error('localStorage is not available:', e);
      return false;
    }
  }

  private parseJsonSafely(key: string) {
    try {
      return JSON.parse(key);
    } catch (e) {
      if ((e as Error).name !== 'SyntaxError') {
        throw e;
      }
      return key.trim();
    }
  }

  private isLocalStorageAvailable(): boolean {
    if (!this.isClient) return false;
    if (this.storageAvailable !== null) return this.storageAvailable;

    this.storageAvailable = SafeLocalStorage.isStorageAvailable();
    return this.storageAvailable;
  }

  public get(): V | null {
    if (!this.isLocalStorageAvailable()) return null;

    try {
      const value = localStorage.getItem(this.key);
      return value ? this.parseJsonSafely(value) : null;
    } catch (e) {
      console.error(`Failed to get item from localStorage for key "${this.key}":`, e);
      return null;
    }
  }

  public set(value: V): boolean {
    if (!this.isLocalStorageAvailable()) return false;

    try {
      localStorage.setItem(this.key, JSON.stringify(value));
      return true;
    } catch (e) {
      console.error(`Failed to set item in localStorage for key "${this.key}":`, e);
      return false;
    }
  }

  public remove(): boolean {
    if (!this.isLocalStorageAvailable()) return false;

    try {
      localStorage.removeItem(this.key);
      return true;
    } catch (e) {
      console.error(`Failed to remove item from localStorage for key "${this.key}":`, e);
      return false;
    }
  }
}

const createdSafeLocalStorage: { [key: string]: SafeLocalStorage<any> } = {};

export const createSafeLocalStorage = <T>(key: string, defaultValue?: T) => {
  if (createdSafeLocalStorage[key]) return createdSafeLocalStorage[key];

  const newlyCreatedStorage = new SafeLocalStorage<T>(key, defaultValue);
  createdSafeLocalStorage[key] = newlyCreatedStorage;
  return newlyCreatedStorage;
};

export default SafeLocalStorage;
